import React, { FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ClipboardList, History, MapPin, Radio, RefreshCw, X } from 'lucide-react';
import PdaPage from '../components/PdaPage';
import QrmarkCameraScanner from '../components/QrmarkCameraScanner';
import type { QrmarkCameraPayload } from '../components/QrmarkCameraScanner';
import PmiElementComparison, {
  PmiElementComparisonItem,
  PmiElementFilter,
} from '../components/PmiElementComparison';
import {
  bindFinalReleasePmiSpectrum,
  CustomerTechSpecElementItem,
  CustomerTechSpecElementListData,
  FinalReleasePmiRequirement,
  FinalReleasePmiSpectrumBindPayload,
  FinalReleasePmiSpectrumBindResult,
  FinalReleasePmiTraceScanResult,
  listCustomerTechSpecElements,
  listFinalReleasePmiRequirements,
  PmiGroupScanResult,
  PmiRebindScanData,
  PmiSpectrumBindPayload,
  scanPmiGroup,
  scanPmiRebind,
  scanFinalReleasePmiTrace,
} from '../services/api';
import { usePersistentFocus } from '../hooks/usePersistentFocus';
import { navigateTo } from '../utils/navigation';
import {
  extractDeliveryBusinessCode,
  FORMAL_TRACE_RE,
  normalizeScannedCode,
  PMIG_RE,
} from '../utils/pdaCodes';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifyInfo, notifySuccess } from '../utils/pdaFeedback';
import {
  buildPmiElementComparisonItems,
  hasPmiRangeSource,
  readPmiRangeFromRecord,
} from '../utils/pmiElementComparison';
import {
  buildPmiSpectrumPayloadFromWsData,
  buildPmiSpectrumPayloadKey,
  buildPmiSpectrumWsUrl,
  DEFAULT_PMI_SPECTRUM_WS_URL,
  isPmiGroupSpectrumWsDataFresh,
  isPmiSpectrumWsDataFresh,
} from '../utils/xrfSpectrum';

type ContinuousPmiStage = 'alreadyDetected' | 'choice' | 'fail' | 'noPmi' | 'pass' | 'recognized' | 'scanning' | 'testing';
type ElementTone = 'pass' | 'fail' | 'unknown';
type ScanOutcome = 'alreadyDetected' | 'failed' | 'noPmi' | 'passed' | 'waiting';
type ScannerFeedbackTone = 'error' | 'info' | 'ready' | 'success';
type UnifiedPmiMode = 'early' | 'formal' | 'unset';

interface ProductInfoField {
  label: string;
  value: string;
}

interface ProductInfoDisplay {
  fields: ProductInfoField[];
  grade: string;
  name: string;
}

interface PmiScanInputParseResult {
  code: string;
  errorMessage: string;
}

interface PendingTraceScan {
  code: string;
  result: FinalReleasePmiTraceScanResult;
}

interface PendingPmiRebind {
  code: string;
  validation: PmiRebindScanData;
}

interface ScannedPmiGroup {
  code: string;
  pmiInspection?: unknown;
  result: PmiGroupScanResult;
}

interface LastCompletedPmiInspection {
  code: string;
  result: FinalReleasePmiTraceScanResult;
  tone: 'fail' | 'pass';
}

interface PdaScanBridgeWindow extends Window {
  XSOSPdaScan?: (payload: unknown) => void;
  onPdaScanResult?: (payload: unknown) => void;
}

interface XrfDevice {
  deviceId: string;
}

const PMI_SPECTRUM_WS_URL = process.env.REACT_APP_PMI_SPECTRUM_WS_URL || DEFAULT_PMI_SPECTRUM_WS_URL;
const PMI_SPECTRUM_DEVICES_URL = process.env.REACT_APP_PMI_SPECTRUM_DEVICES_URL || 'https://api.yanclex.com/devices';
const DEVICE_LIST_REFRESH_MS = 10000;
const SPECTRUM_DEDUPE_WINDOW_MS = 15000;
const SPECTRUM_WS_RETRY_MIN_MS = 1000;
const SPECTRUM_WS_RETRY_MAX_MS = 15000;
const MAX_PMIG_CODES_PER_SPECTRUM = 200;
const SCAN_AUTO_SUBMIT_DELAY_MS = 400;
const SCAN_INPUT_DEDUPE_WINDOW_MS = 1200;
const TRACE_CODE_RE = /^\d{8}$/;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown) {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return '';
}

function extractCustomerTechSpecElements(data?: CustomerTechSpecElementItem[] | CustomerTechSpecElementListData) {
  if (Array.isArray(data)) {
    return data;
  }

  for (const items of [data?.elements, data?.items, data?.list, data?.data]) {
    if (Array.isArray(items)) {
      return items;
    }
  }
  return [];
}

function serializeTechSpecPmiRanges(items: CustomerTechSpecElementItem[]) {
  const records = items.map(asRecord).filter((record): record is Record<string, unknown> => Boolean(record));
  const hasRequiredFlags = records.some((record) => (
    record.pmi_required !== undefined && record.pmi_required !== null && record.pmi_required !== ''
  ));
  const ranges = records.flatMap((record) => {
    const requiredText = asString(record.pmi_required).toLowerCase();
    const isRequired = record.pmi_required === true
      || record.pmi_required === 1
      || ['1', 'true', 'yes', 'required', 'enabled', '是', '启用'].includes(requiredText);
    if (hasRequiredFlags && !isRequired) {
      return [];
    }

    const element = asString(record.element || record.symbol || record.element_code || record.code || record.name);
    const range = readPmiRangeFromRecord(record);
    return element && range && (range.min !== undefined || range.max !== undefined)
      ? [{ element, pmi_max: range.max, pmi_min: range.min }]
      : [];
  });
  return ranges.length ? JSON.stringify(ranges) : '';
}

function readNestedPmiValue(source: unknown, keys: string[], depth = 0): unknown {
  const record = asRecord(source);
  if (!record || depth > 3) {
    return undefined;
  }

  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  const nestedSources = [
    record.current_unit,
    record.currentUnit,
    record.group,
    record.task,
    record.requirement,
    record.pmi_requirement,
  ];
  for (const nested of nestedSources) {
    const value = readNestedPmiValue(nested, keys, depth + 1);
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

function getPmiInspectionSource(result: unknown) {
  return readNestedPmiValue(result, [
    'pmi_inspection',
    'pmiInspection',
    'pmi_ranges',
    'pmi_range',
    'requirement_elements',
    'pmi_requirement_elements',
  ]);
}

function getCustomerTechSpecId(result: unknown) {
  return asString(readNestedPmiValue(result, ['customer_tech_spec_id', 'customerTechSpecId']));
}

function getPmiRequirementId(result: unknown) {
  return asString(readNestedPmiValue(result, ['pmi_requirement_id', 'requirement_id']));
}

function extractPmiRequirementList(data?: {
  items?: FinalReleasePmiRequirement[];
  list?: FinalReleasePmiRequirement[];
  data?: FinalReleasePmiRequirement[];
}) {
  for (const items of [data?.items, data?.list, data?.data]) {
    if (Array.isArray(items)) {
      return items;
    }
  }
  return [];
}

function applyPmiInspectionSource(result: FinalReleasePmiTraceScanResult, pmiInspection: unknown) {
  return hasPmiRangeSource(pmiInspection)
    ? { ...result, pmi_inspection: pmiInspection }
    : result;
}

function extractXrfDevices(data: unknown) {
  const record = asRecord(data);
  const source = Array.isArray(data) ? data : record?.devices;
  if (!Array.isArray(source)) {
    return [];
  }

  return source.reduce<XrfDevice[]>((devices, item) => {
    const device = asRecord(item);
    const deviceId = asString(device?.deviceId || device?.device_id || device?.id);
    if (deviceId) {
      devices.push({ deviceId });
    }
    return devices;
  }, []);
}

function readPmiTraceCodeFromScanText(value: string): PmiScanInputParseResult {
  const trimmed = value.trim();
  const buildResult = (rawCode: string): PmiScanInputParseResult => {
    const code = normalizeScannedCode(rawCode);
    if (!code) {
      return {
        code: '',
        errorMessage: '',
      };
    }

    if (!TRACE_CODE_RE.test(code)) {
      return {
        code: '',
        errorMessage: '未识别到8位追溯码，请扫描产品标签隐写码',
      };
    }

    return {
      code,
      errorMessage: '',
    };
  };

  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const payload = asRecord(JSON.parse(trimmed));

      if (payload && Object.prototype.hasOwnProperty.call(payload, 'result')) {
        const resultText = asString(payload.result);
        if (!resultText || resultText.toLowerCase() === 'null') {
          return {
            code: '',
            errorMessage: '未识别到追溯码，请重新扫描标签',
          };
        }

        return buildResult(resultText);
      }
    } catch {
      return buildResult(value);
    }
  }

  return buildResult(value);
}

function readUnifiedPmiCodeFromScanText(value: string): PmiScanInputParseResult {
  const businessCode = extractDeliveryBusinessCode(value);
  if (PMIG_RE.test(businessCode) || FORMAL_TRACE_RE.test(businessCode)) {
    return {
      code: businessCode,
      errorMessage: '',
    };
  }

  return readPmiTraceCodeFromScanText(value);
}

function logPmiScannerPayload(value: string) {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return;
  }

  try {
    const payload = asRecord(JSON.parse(trimmed));
    if (!payload) {
      return;
    }

    console.info('[PMI scanner payload]', {
      error: payload.error,
      ok: payload.ok,
      qrText: payload.qrText,
      result: payload.result,
    });
  } catch {
    // Non-JSON scanner input is handled by the normal code parser.
  }
}

function serializePdaScanPayload(payload: unknown) {
  if (typeof payload === 'string') {
    return payload;
  }
  if (typeof payload === 'number') {
    return String(payload);
  }
  try {
    return JSON.stringify(payload);
  } catch {
    return '';
  }
}

function extractPdaScanMessagePayload(value: unknown): unknown {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      const extracted = extractPdaScanMessagePayload(parsed);
      if (extracted === null) {
        return null;
      }
      return extracted === parsed ? value : extracted;
    } catch {
      return null;
    }
  }

  const record = asRecord(value);
  if (!record) {
    return null;
  }
  if (Object.prototype.hasOwnProperty.call(record, 'result')
    || Object.prototype.hasOwnProperty.call(record, 'qrText')) {
    return record;
  }

  const messageType = asString(record.type || record.event).toLowerCase();
  if (['pda-scan', 'pda_scan', 'xsos:pda-scan', 'xsos_pda_scan'].includes(messageType)) {
    return record.payload ?? record.data ?? null;
  }
  return null;
}

function normalizeJudgement(value: unknown): ElementTone {
  const text = asString(value).toLowerCase();
  if (!text) {
    return 'unknown';
  }

  if (['false', 'fail', 'failed', 'ng', 'no', 'out', 'out_of_range', 'reject', 'rejected'].includes(text)
    || text.includes('不合格')
    || text.includes('异常')
    || text.includes('超')) {
    return 'fail';
  }

  if (['true', 'pass', 'passed', 'ok', 'qualified', 'accept', 'accepted'].includes(text)
    || text.includes('合格')
    || text.includes('正常')) {
    return 'pass';
  }

  return 'unknown';
}

function readResultTone(record: Record<string, unknown> | null): ElementTone {
  if (!record) {
    return 'unknown';
  }

  const keys = [
    'passed',
    'pass',
    'qualified',
    'is_qualified',
    'final_result',
    'inspection_result',
    'pmi_result',
    'quality_result',
    'judge_result',
    'result',
    'status',
  ];

  for (const key of keys) {
    if (key in record) {
      const tone = normalizeJudgement(record[key]);
      if (tone !== 'unknown') {
        return tone;
      }
    }
  }

  return 'unknown';
}

function readFlag(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return null;
  }

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    if (typeof value === 'string' && value.trim()) {
      const normalized = value.trim().toLowerCase();
      if (['true', 'yes', 'y', '1', 'required', 'need', 'needed'].includes(normalized)) {
        return true;
      }
      if (['false', 'no', 'n', '0', 'not_required', 'none'].includes(normalized)) {
        return false;
      }
    }
  }

  return null;
}

function includesAnyText(record: Record<string, unknown> | null, keys: string[], needles: string[]) {
  if (!record) {
    return false;
  }

  return keys.some((key) => {
    const text = asString(record[key]).toLowerCase();
    return text && needles.some((needle) => text.includes(needle));
  });
}

function getPmiStatus(result: FinalReleasePmiTraceScanResult | null) {
  return asString(result?.pmi_status).toLowerCase();
}

function getTraceCode(result: FinalReleasePmiTraceScanResult | null, fallback = '') {
  const record = asRecord(result);
  const currentUnit = asRecord(record?.current_unit);
  return asString(
    result?.trace_code
      || record?.traceCode
      || currentUnit?.trace_code
      || currentUnit?.inspection_serial
      || currentUnit?.serial_no
      || currentUnit?.serial,
  ) || fallback;
}

function getTraceGrade(result: FinalReleasePmiTraceScanResult | null) {
  const record = asRecord(result);
  const currentUnit = asRecord(record?.current_unit);
  const requirement = asRecord(record?.requirement);
  const task = asRecord(record?.task);
  return asString(
    result?.grade
      || result?.material_grade
      || currentUnit?.grade
      || currentUnit?.material_grade
      || requirement?.material_grade
      || task?.material_grade,
  );
}

function readFirstString(records: Array<Record<string, unknown> | null>, keys: string[]) {
  for (const record of records) {
    if (!record) {
      continue;
    }

    for (const key of keys) {
      const value = asString(record[key]);
      if (value) {
        return value;
      }
    }
  }

  return '';
}

function getProductInfo(result: FinalReleasePmiTraceScanResult | null, fallbackCode: string): ProductInfoDisplay | null {
  const record = asRecord(result);
  if (!record) {
    return null;
  }

  const currentUnit = asRecord(record.current_unit);
  const product = asRecord(record.product) || asRecord(currentUnit?.product);
  const item = asRecord(record.item) || asRecord(currentUnit?.item);
  const material = asRecord(record.material) || asRecord(currentUnit?.material);
  const task = asRecord(record.task);
  const trace = asRecord(record.trace);
  const requirement = asRecord(record.requirement) || asRecord(record.pmi_requirement);
  const records = [record, requirement, trace, currentUnit, product, item, material, task];
  const traceCode = getTraceCode(result, fallbackCode);
  if (!traceCode) {
    return null;
  }

  const name = readFirstString(records, [
    'product_name',
    'productName',
    'product',
    'part_name',
    'partName',
    'item_name',
    'itemName',
    'workpiece_name',
    'workpieceName',
    'name',
  ]) || '产品信息';
  const grade = getTraceGrade(result);
  const spec = readFirstString(records, [
    'spec',
    'specification',
    'product_spec',
    'productSpec',
    'contract_spec',
    'contractSpec',
    'model',
    'model_spec',
    'size',
  ]);
  const heatNo = readFirstString(records, [
    'heat_no',
    'heatNo',
    'smelt_heat_no',
    'smeltHeatNo',
    'furnace_no',
    'furnaceNo',
    'steel_heat_no',
    'steelHeatNo',
    'sy_no',
    'syNo',
  ]);
  const batchNo = readFirstString(records, [
    'batch_no',
    'batchNo',
    'lot_no',
    'lotNo',
    'production_batch_no',
    'productionBatchNo',
    'inspection_batch_no',
    'inspectionBatchNo',
    'final_release_no',
    'finalReleaseNo',
  ]);
  const customerCode = readFirstString(records, ['customer_code', 'customerCode']);
  const customerSpecNo = readFirstString(records, ['customer_spec_no', 'customerSpecNo']);
  const orderNo = readFirstString(records, ['order_no', 'orderNo', 'customer_order_no', 'customerOrderNo']);
  const lineNo = readFirstString(records, ['line_no', 'lineNo']);
  const deliveryNoteNo = readFirstString(records, ['delivery_note_no', 'deliveryNoteNo']);
  const taskNo = readFirstString(records, ['task_no', 'taskNo', 'task_code', 'taskCode', 'pmi_task_no']);
  const inspectionSerial = readFirstString(records, ['inspection_serial', 'inspectionSerial']);

  const fields = [
    { label: '追溯码', value: traceCode },
    { label: '客户', value: customerCode },
    { label: '订单号', value: orderNo },
    { label: '行号', value: lineNo },
    { label: '规格', value: spec },
    { label: '炉号', value: heatNo },
    { label: '批次', value: batchNo },
    { label: '客户规范', value: customerSpecNo },
    { label: '送货单', value: deliveryNoteNo },
    { label: 'PMI任务', value: taskNo },
    { label: '件位流水号', value: inspectionSerial },
  ].filter((field) => Boolean(field.value));

  return {
    fields,
    grade,
    name,
  };
}

function resolveScanOutcome(result: FinalReleasePmiTraceScanResult | null): ScanOutcome {
  const resultRecord = asRecord(result);
  const unitRecord = asRecord(resultRecord?.current_unit);
  const status = getPmiStatus(result);
  const pmiRequired = readFlag(resultRecord, ['requires_pmi', 'pmi_required', 'need_pmi', 'needs_pmi', 'required'])
    ?? readFlag(unitRecord, ['requires_pmi', 'pmi_required', 'need_pmi', 'needs_pmi', 'required']);

  if (pmiRequired === false
    || ['not_required', 'no_pmi', 'none'].includes(status)
    || includesAnyText(resultRecord, ['message', 'msg', 'status', 'status_text', 'pmi_status'], ['无需pmi', '无需 pmi', '无需检测', 'not required', 'no pmi'])
    || includesAnyText(unitRecord, ['message', 'msg', 'status', 'status_text', 'pmi_status'], ['无需pmi', '无需 pmi', '无需检测', 'not required', 'no pmi'])) {
    return 'noPmi';
  }

  if (result?.already_bound) {
    return 'alreadyDetected';
  }

  if (status === 'passed') {
    return 'passed';
  }

  if (status === 'failed') {
    return 'failed';
  }

  return 'waiting';
}

function resolveInspectionTone(result: FinalReleasePmiTraceScanResult | null): ElementTone {
  const status = getPmiStatus(result);
  if (status === 'failed') {
    return 'fail';
  }
  if (status === 'passed') {
    return 'pass';
  }

  const resultTone = readResultTone(asRecord(result));
  if (resultTone !== 'unknown') {
    return resultTone;
  }

  const items = buildPmiElementComparisonItems(result);
  if (items.some((item) => item.state === 'fail')) {
    return 'fail';
  }

  if (items.length && items.every((item) => item.state === 'pass')) {
    return 'pass';
  }

  return 'pass';
}

function getStatusText(stage: ContinuousPmiStage) {
  if (stage === 'alreadyDetected') {
    return '已检测';
  }
  if (stage === 'noPmi') {
    return '无需 PMI';
  }
  if (stage === 'recognized') {
    return '已识别';
  }
  if (stage === 'testing') {
    return '等待检测';
  }
  if (stage === 'choice') {
    return '请选择处理方式';
  }
  if (stage === 'pass') {
    return '检测通过';
  }
  if (stage === 'fail') {
    return '检测不通过';
  }
  return '请扫描标签';
}

function getSubText(stage: ContinuousPmiStage, canRetest = false) {
  if (stage === 'alreadyDetected') {
    return canRetest ? '查看记录 / 复检 / 继续扫描' : '查看记录 / 继续扫描';
  }
  if (stage === 'noPmi') {
    return '直接扫描下一张标签';
  }
  if (stage === 'recognized') {
    return '标签已识别';
  }
  if (stage === 'testing') {
    return '保持页面，使用光谱枪检测';
  }
  if (stage === 'choice') {
    return '新标签追加或替换当前标签';
  }
  if (stage === 'pass') {
    return '继续扫描下一张标签';
  }
  if (stage === 'fail') {
    return '已记录异常，请查看元素列表';
  }
  return '对准产品标签自动识别';
}

function getSerialText(result: FinalReleasePmiTraceScanResult | null, scanCode: string) {
  return getTraceCode(result, scanCode) || '8位追溯码';
}

function normalizeSpectrumElements(elements: Record<string, number | string>) {
  const normalized = Object.keys(elements).reduce<Record<string, number>>((result, key) => {
    const numericValue = Number(elements[key]);
    if (Number.isFinite(numericValue)) {
      result[key] = numericValue;
    }
    return result;
  }, {});

  if (!Object.keys(normalized).length) {
    throw new Error('光谱结果没有可绑定的元素数据');
  }
  return normalized;
}

function toUnifiedSpectrumPayload(
  payload: PmiSpectrumBindPayload,
  mode: UnifiedPmiMode,
  traceCodes: string[],
  pmigCodes: string[],
): FinalReleasePmiSpectrumBindPayload {
  const { inspection_serial: _inspectionSerial, ...basePayload } = payload;
  const normalizedTraceCodes = Array.from(new Set(
    traceCodes.map((code) => code.trim()).filter((code) => TRACE_CODE_RE.test(code)),
  ));
  const normalizedPmigCodes = Array.from(new Set(
    pmigCodes.map((code) => code.trim()).filter((code) => PMIG_RE.test(code)),
  ));
  const spectrumPayload = {
    ...basePayload,
    elements: normalizeSpectrumElements(payload.elements),
    measured_at: payload.measured_at || new Date().toISOString(),
    method: 'PMI',
  };

  if (mode === 'formal') {
    if (!normalizedTraceCodes.length) {
      throw new Error('正式检测轮次缺少 80×80 标签');
    }
    if (normalizedPmigCodes.length > 1) {
      throw new Error('正式检测最多追加一个 60×60 PMIG');
    }
    return {
      ...spectrumPayload,
      trace_codes: normalizedTraceCodes,
      ...(normalizedPmigCodes[0] ? { pmig_code: normalizedPmigCodes[0] } : {}),
    };
  }

  if (mode === 'early') {
    if (normalizedTraceCodes.length) {
      throw new Error('提前检测轮次不能加入 80×80 标签');
    }
    if (!normalizedPmigCodes.length) {
      throw new Error('提前检测轮次缺少 60×60 PMIG 标签');
    }
    if (normalizedPmigCodes.length > MAX_PMIG_CODES_PER_SPECTRUM) {
      throw new Error(`一次光谱最多绑定 ${MAX_PMIG_CODES_PER_SPECTRUM} 个 60×60 PMIG`);
    }
    return { ...spectrumPayload, pmig_codes: normalizedPmigCodes };
  }

  throw new Error('当前没有待绑定标签');
}

function hasWaitingPmiGroup(result: PmiGroupScanResult) {
  return result.scan_type === 'group'
    && result.group.status === 'awaiting'
    && result.pmi_status === 'waiting';
}

function toPmiGroupHistoryResult(
  result: PmiGroupScanResult,
  fallbackCode: string,
): FinalReleasePmiTraceScanResult {
  return {
    ...result,
    already_bound: Boolean(result.has_pmi_data || result.already_bound),
    can_retest: false,
    requires_pmi: result.requires_pmi ?? true,
    trace_code: result.pmig_code || fallbackCode,
  };
}

function pmiBindRecordFailed(value: unknown) {
  const record = asRecord(value);
  if (!record) {
    return false;
  }
  const passed = record.passed ?? record.pass ?? record.qualified;
  if (passed === false || passed === 0 || ['false', '0'].includes(asString(passed).toLowerCase())) {
    return true;
  }
  const finalResult = asString(
    record.final_result ?? record.result ?? record.status ?? record.pmi_status,
  ).toLowerCase();
  return ['fail', 'failed', 'ng', 'unqualified', '不合格'].includes(finalResult);
}

function unifiedPmiBindFailed(result: FinalReleasePmiSpectrumBindResult) {
  const resultRecord = asRecord(result) || {};
  if (Array.isArray(resultRecord.groups) && resultRecord.groups.length > 0) {
    return resultRecord.groups.some(pmiBindRecordFailed);
  }
  const failedCount = Number(resultRecord.failed_count ?? resultRecord.failedCount ?? 0);
  if (Number.isFinite(failedCount) && failedCount > 0) {
    return true;
  }
  if (Array.isArray(resultRecord.failures) && resultRecord.failures.length > 0) {
    return true;
  }
  if (pmiBindRecordFailed(resultRecord.unit)) {
    return true;
  }
  if (Array.isArray(resultRecord.units) && resultRecord.units.some(pmiBindRecordFailed)) {
    return true;
  }
  return pmiBindRecordFailed(resultRecord);
}

function getPmiFlowErrorMessage(error: unknown, fallback: string) {
  const errorRecord = asRecord(error);
  const response = asRecord(errorRecord?.response);
  const responseData = asRecord(response?.data);
  const nestedData = asRecord(errorRecord?.data);
  const message = asString(
    responseData?.msg
      || responseData?.message
      || nestedData?.msg
      || nestedData?.message
      || errorRecord?.msg,
  ) || getPdaErrorMessage(error, fallback);
  if (/不同预送货单|不同送货单|不同物料|不同\s*SY/i.test(message)) {
    return `${message}，请清空本次扫码后重新扫码`;
  }
  if (/同一明细行不能合并|同一明细.*重复|重复标签/i.test(message)) {
    return '扫到了同一明细行的重复标签，请清空本次扫码后重新扫码';
  }
  return message;
}

function ContinuousPmiScan() {
  const inputRef = useRef<HTMLInputElement>(null);
  const manualInputRef = useRef<HTMLInputElement>(null);
  const deviceSelectRef = useRef<HTMLSelectElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const lastPayloadRef = useRef<{ key: string; time: number } | null>(null);
  const passTimerRef = useRef(0);
  const manualInputModeTimerRef = useRef(0);
  const deviceListLoadedRef = useRef(false);
  const scanSubmitTimerRef = useRef(0);
  const scanKeyboardTimerRef = useRef(0);
  const scanKeyboardBufferRef = useRef('');
  const scanSubmittingRef = useRef(false);
  const bindingRef = useRef(false);
  const stageRef = useRef<ContinuousPmiStage>('scanning');
  const traceCodesRef = useRef<string[]>([]);
  const pmiGroupsRef = useRef<ScannedPmiGroup[]>([]);
  const scanResultRef = useRef<FinalReleasePmiTraceScanResult | null>(null);
  const waitStartedAtRef = useRef(0);
  const bindSpectrumPayloadRef = useRef<(payload: PmiSpectrumBindPayload) => Promise<void>>(async () => undefined);
  const lastSubmittedScanRef = useRef<{ rawText: string; time: number } | null>(null);
  const submitScanTextRef = useRef<(rawText: string) => Promise<void>>(async () => undefined);
  const pmiInspectionCacheRef = useRef(new Map<string, string>());
  const requirementInspectionCacheRef = useRef(new Map<string, string>());
  const [scanText, setScanText] = useState('');
  const [manualTraceCode, setManualTraceCode] = useState('');
  const [manualInputMode, setManualInputMode] = useState<'none' | 'numeric'>('none');
  const [scannerFeedback, setScannerFeedback] = useState('');
  const [scannerFeedbackTone, setScannerFeedbackTone] = useState<ScannerFeedbackTone>('ready');
  const [scannerFocused, setScannerFocused] = useState(false);
  const [cameraScannerOpen, setCameraScannerOpen] = useState(false);
  const [scanCode, setScanCode] = useState('');
  const [traceCodes, setTraceCodes] = useState<string[]>([]);
  const [pmiMode, setPmiMode] = useState<UnifiedPmiMode>('unset');
  const [pmiGroups, setPmiGroups] = useState<ScannedPmiGroup[]>([]);
  const [reboundPmigCodes, setReboundPmigCodes] = useState<string[]>([]);
  const [stage, setStage] = useState<ContinuousPmiStage>('scanning');
  const [scanResult, setScanResult] = useState<FinalReleasePmiTraceScanResult | null>(null);
  const [pendingTraceScan, setPendingTraceScan] = useState<PendingTraceScan | null>(null);
  const [pendingPmiRebind, setPendingPmiRebind] = useState<PendingPmiRebind | null>(null);
  const [pendingSpectrumPayload, setPendingSpectrumPayload] = useState<PmiSpectrumBindPayload | null>(null);
  const [elementFilter, setElementFilter] = useState<PmiElementFilter>('all');
  const [lastCompletedInspection, setLastCompletedInspection] = useState<LastCompletedPmiInspection | null>(null);
  const [lastElementFilter, setLastElementFilter] = useState<PmiElementFilter>('all');
  const [xrfDevices, setXrfDevices] = useState<XrfDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceRefreshKey, setDeviceRefreshKey] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [wsReconnecting, setWsReconnecting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [binding, setBinding] = useState(false);
  const [waitStartedAt, setWaitStartedAt] = useState(0);

  const comparisonItems = useMemo<PmiElementComparisonItem[]>(() => (
    buildPmiElementComparisonItems(scanResult)
  ), [scanResult]);
  const lastComparisonItems = useMemo<PmiElementComparisonItem[]>(() => (
    buildPmiElementComparisonItems(lastCompletedInspection?.result)
  ), [lastCompletedInspection]);
  const canScanLabel = !scanning && !binding && !pendingTraceScan && !pendingPmiRebind && !pendingSpectrumPayload
    && !['alreadyDetected', 'fail', 'pass'].includes(stage);
  const isInputEnabled = canScanLabel && !cameraScannerOpen;

  const focusScannerInput = useCallback((delayMs = 0) => {
    if (!isInputEnabled) {
      return;
    }

    window.setTimeout(() => {
      if (document.activeElement === deviceSelectRef.current) {
        return;
      }
      (manualInputRef.current || inputRef.current)?.focus({ preventScroll: true });
    }, delayMs);
  }, [isInputEnabled]);

  usePersistentFocus(manualInputRef, isInputEnabled, `${stage}-${scanCode}`);

  useLayoutEffect(() => {
    if (!isInputEnabled) {
      return undefined;
    }

    const timers: number[] = [];
    const getFocusTarget = () => manualInputRef.current || inputRef.current;
    const hasStartedScanning = () => (
      Boolean(getFocusTarget()?.value)
      || Boolean(scanKeyboardBufferRef.current)
      || scanSubmittingRef.current
    );
    const focusNativeScannerInput = () => {
      const input = getFocusTarget();
      if (!input || hasStartedScanning()) {
        return;
      }
      input.focus({ preventScroll: true });
      try {
        input.setSelectionRange(input.value.length, input.value.length);
      } catch {
        // Some Android WebViews do not expose selection APIs for scanner inputs.
      }
      setScannerFocused(document.activeElement === input);
    };
    const rearmNativeScannerInput = () => {
      const input = getFocusTarget();
      if (!input || hasStartedScanning()) {
        return;
      }
      input.blur();
      timers.push(window.setTimeout(focusNativeScannerInput, 24));
    };

    focusNativeScannerInput();
    [120, 420, 900].forEach((delayMs) => {
      timers.push(window.setTimeout(rearmNativeScannerInput, delayMs));
    });

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [isInputEnabled, scanCode, stage]);

  useEffect(() => {
    if (typeof fetch !== 'function') {
      return undefined;
    }

    let active = true;
    let refreshTimer: number | undefined;

    const loadDevices = (isForegroundLoad: boolean) => {
      if (isForegroundLoad) {
        setDeviceLoading(true);
      }
      void fetch(PMI_SPECTRUM_DEVICES_URL)
        .then((response) => {
          if (!response.ok) {
            throw new Error('光谱仪设备列表加载失败');
          }
          return response.json();
        })
        .then((data) => {
          if (!active) {
            return;
          }
          // 操作员正在下拉框里选设备时不要动列表，否则原生 picker 会被顶掉。
          if (!isForegroundLoad && document.activeElement === deviceSelectRef.current) {
            return;
          }
          const devices = extractXrfDevices(data);
          setXrfDevices((current) => (
            current.length === devices.length
              && current.every((device, index) => device.deviceId === devices[index].deviceId)
              ? current
              : devices
          ));
          setSelectedDeviceId((current) => (
            devices.some((device) => device.deviceId === current) ? current : devices[0]?.deviceId || ''
          ));
        })
        .catch(() => {
          // 后台刷新失败保留上一次的列表，只有首次加载失败才显示"暂无可用设备"。
          if (active && !deviceListLoadedRef.current) {
            setXrfDevices([]);
            setSelectedDeviceId('');
          }
        })
        .finally(() => {
          if (active) {
            deviceListLoadedRef.current = true;
            if (isForegroundLoad) {
              setDeviceLoading(false);
            }
            refreshTimer = window.setTimeout(() => loadDevices(false), DEVICE_LIST_REFRESH_MS);
          }
        });
    };

    loadDevices(true);

    return () => {
      active = false;
      if (refreshTimer !== undefined) {
        window.clearTimeout(refreshTimer);
      }
    };
  }, [deviceRefreshKey]);

  useEffect(() => {
    if (!isInputEnabled) {
      return undefined;
    }

    const restoreFocus = () => focusScannerInput(0);
    const restoreVisibleFocus = () => {
      if (!document.hidden) {
        focusScannerInput(40);
      }
    };

    document.addEventListener('visibilitychange', restoreVisibleFocus);
    window.addEventListener('focus', restoreFocus);
    window.addEventListener('pointerdown', restoreFocus, true);

    return () => {
      document.removeEventListener('visibilitychange', restoreVisibleFocus);
      window.removeEventListener('focus', restoreFocus);
      window.removeEventListener('pointerdown', restoreFocus, true);
    };
  }, [focusScannerInput, isInputEnabled]);

  const resetForNextScan = useCallback(() => {
    window.clearTimeout(passTimerRef.current);
    window.clearTimeout(scanSubmitTimerRef.current);
    setScanText('');
    setManualTraceCode('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setScannerFeedback('');
    setScannerFeedbackTone('ready');
    setScanCode('');
    setTraceCodes([]);
    setPmiMode('unset');
    setPmiGroups([]);
    setReboundPmigCodes([]);
    setPendingTraceScan(null);
    setPendingPmiRebind(null);
    setPendingSpectrumPayload(null);
    setElementFilter('all');
    setStage('scanning');
    setScanResult(null);
    setBinding(false);
    setWaitStartedAt(0);
    lastPayloadRef.current = null;
    window.setTimeout(() => (manualInputRef.current || inputRef.current)?.focus({ preventScroll: true }), 80);
  }, []);

  const rememberCompletedInspection = useCallback((result: FinalReleasePmiTraceScanResult, fallbackCode = '') => {
    const tone = resolveInspectionTone(result) === 'fail' ? 'fail' : 'pass';
    setLastCompletedInspection({
      code: getTraceCode(result, fallbackCode),
      result,
      tone,
    });
    setLastElementFilter('all');
  }, []);

  const settleBindResult = useCallback((result: FinalReleasePmiTraceScanResult) => {
    setScanResult(result);
    setPendingSpectrumPayload(null);
    const tone = resolveInspectionTone(result);

    if (tone === 'fail') {
      const items = buildPmiElementComparisonItems(result);
      setElementFilter(items.some((item) => item.state === 'fail') ? 'fail' : 'all');
      setStage('fail');
      notifyError('检测不合格');
      return;
    }

    rememberCompletedInspection(result);
    notifySuccess('检测合格');
    resetForNextScan();
  }, [rememberCompletedInspection, resetForNextScan]);

  async function loadTechSpecPmiInspection(techSpecId: string) {
    if (!techSpecId) {
      return '';
    }

    const cached = pmiInspectionCacheRef.current.get(techSpecId);
    if (cached !== undefined) {
      return cached;
    }

    try {
      const response = await listCustomerTechSpecElements(techSpecId);
      const pmiInspection = serializeTechSpecPmiRanges(extractCustomerTechSpecElements(response.data));
      pmiInspectionCacheRef.current.set(techSpecId, pmiInspection);
      return pmiInspection;
    } catch {
      pmiInspectionCacheRef.current.set(techSpecId, '');
      return '';
    }
  }

  async function resolvePmiInspectionSource(result: unknown, traceCode: string) {
    const embedded = getPmiInspectionSource(result);
    if (hasPmiRangeSource(embedded)) {
      return embedded;
    }

    const techSpecId = getCustomerTechSpecId(result);
    if (techSpecId) {
      return await loadTechSpecPmiInspection(techSpecId) || embedded;
    }

    const requirementId = getPmiRequirementId(result);
    const lookupKey = requirementId || traceCode;
    if (!lookupKey) {
      return embedded;
    }

    const cached = requirementInspectionCacheRef.current.get(lookupKey);
    if (cached !== undefined) {
      return cached || embedded;
    }

    try {
      const response = await listFinalReleasePmiRequirements({
        keyword: lookupKey,
        page: 1,
        page_size: 20,
      });
      const requirements = extractPmiRequirementList(response.data);
      const requirement = requirements.find((item) => (
        !requirementId
        || asString(item.id) === requirementId
        || asString(item.pmi_requirement_id) === requirementId
        || asString(item.requirement_id) === requirementId
      )) || (requirements.length === 1 ? requirements[0] : undefined);
      const requirementInspection = requirement?.pmi_inspection;
      const pmiInspection = hasPmiRangeSource(requirementInspection)
        ? asString(requirementInspection)
        : await loadTechSpecPmiInspection(asString(requirement?.customer_tech_spec_id));
      requirementInspectionCacheRef.current.set(lookupKey, pmiInspection);
      return pmiInspection || embedded;
    } catch {
      requirementInspectionCacheRef.current.set(lookupKey, '');
      return embedded;
    }
  }

  const bindSpectrumPayload = useCallback(async (payload: PmiSpectrumBindPayload) => {
    if (bindingRef.current) {
      return;
    }
    if (!traceCodes.length && !pmiGroups.length) {
      notifyError('请先扫描至少一个 PMI 标签');
      return;
    }
    bindingRef.current = true;
    setBinding(true);
    setStage('testing');

    try {
      const pmigCodes = pmiGroups.map((group) => group.code);
      const response = await bindFinalReleasePmiSpectrum(
        toUnifiedSpectrumPayload(payload, pmiMode, traceCodes, pmigCodes),
      );
      const failed = unifiedPmiBindFailed(response.data);
      const pmiInspection = getPmiInspectionSource(scanResult)
        || pmiGroups.find((group) => hasPmiRangeSource(group.pmiInspection))?.pmiInspection
        || getPmiInspectionSource(pmiGroups[0]?.result);
      settleBindResult({
        ...scanResult,
        ...response.data,
        already_bound: true,
        can_retest: true,
        elements: payload.elements,
        instrument: payload.instrument,
        pmi_status: failed ? 'failed' : 'passed',
        requires_pmi: true,
        scan_type: traceCodes.length ? 'trace' : 'group',
        trace_code: traceCodes[0] || pmiGroups[0]?.code || '',
        ...(hasPmiRangeSource(pmiInspection) ? { pmi_inspection: pmiInspection } : {}),
      } as FinalReleasePmiTraceScanResult);
    } catch (error) {
      const errorMessage = getPmiFlowErrorMessage(error, '光谱结果绑定失败');
      notifyError(errorMessage);
      setScannerFeedback(errorMessage);
      setScannerFeedbackTone('error');
      setPendingSpectrumPayload(null);
      lastPayloadRef.current = null;
      setStage('testing');
    } finally {
      bindingRef.current = false;
      setBinding(false);
    }
  }, [pmiGroups, pmiMode, scanResult, settleBindResult, traceCodes]);

  stageRef.current = stage;
  traceCodesRef.current = traceCodes;
  pmiGroupsRef.current = pmiGroups;
  scanResultRef.current = scanResult;
  waitStartedAtRef.current = waitStartedAt;
  bindSpectrumPayloadRef.current = bindSpectrumPayload;

  useEffect(() => {
    if (!selectedDeviceId) {
      wsRef.current?.close();
      wsRef.current = null;
      setWsConnected(false);
      setWsReconnecting(false);
      return undefined;
    }

    // 手机浏览器把页面切到后台就会掐掉 WebSocket。原来 onclose 只把状态置成 false、
    // 没有任何重连，切回页面后就永远停在「连接中」，光谱结果再也推不过来 —— 现场
    // 表现是「光谱枪打了没反应」，只能手动刷新页面才能恢复。
    //
    // 这里补上退避重连，并在页面重新可见时立刻重连一次：切回来是最常见的触发场景，
    // 不该让操作员干等退避计时器。
    let disposed = false;
    let socketOpen = false;
    let disconnectNotified = false;
    let retryTimer = 0;
    let retryDelay = SPECTRUM_WS_RETRY_MIN_MS;

    const handleMessage = (event: MessageEvent) => {
      const primaryCode = traceCodesRef.current[0] || pmiGroupsRef.current[0]?.code;
      if (!primaryCode || !['recognized', 'testing'].includes(stageRef.current)) {
        return;
      }
      const spectrumIsFresh = traceCodesRef.current.length
        ? isPmiSpectrumWsDataFresh(event.data, waitStartedAtRef.current)
        : isPmiGroupSpectrumWsDataFresh(event.data, waitStartedAtRef.current);
      if (!spectrumIsFresh) {
        return;
      }

      const fallbackGrade = getTraceGrade(scanResultRef.current)
        || pmiGroupsRef.current[0]?.result.requirement?.material_grade
        || '';
      const payload = buildPmiSpectrumPayloadFromWsData(event.data, primaryCode, fallbackGrade);
      if (!payload) {
        return;
      }

      const key = buildPmiSpectrumPayloadKey(payload);
      const now = Date.now();
      const lastPayload = lastPayloadRef.current;
      if (lastPayload?.key === key && now - lastPayload.time < SPECTRUM_DEDUPE_WINDOW_MS) {
        return;
      }
      lastPayloadRef.current = { key, time: now };
      setPendingSpectrumPayload(payload);
      setScannerFeedback('已收到光谱结果，正在自动提交');
      setScannerFeedbackTone('success');
      notifySuccess('已收到光谱结果，正在自动提交');
      void bindSpectrumPayloadRef.current(payload);
    };

    const scheduleReconnect = () => {
      if (disposed) {
        return;
      }
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(() => {
        retryDelay = Math.min(retryDelay * 2, SPECTRUM_WS_RETRY_MAX_MS);
        connect();
      }, retryDelay);
    };

    function connect() {
      if (disposed) {
        return;
      }
      window.clearTimeout(retryTimer);
      socketOpen = false;

      // 先把 ref 摘掉再关旧连接，旧 socket 的回调就会被下面的守卫拦住。
      const previous = wsRef.current;
      wsRef.current = null;
      previous?.close();

      const socket = new WebSocket(buildPmiSpectrumWsUrl({
        deviceId: selectedDeviceId,
        explicitUrl: PMI_SPECTRUM_WS_URL,
        location: window.location,
      }));
      wsRef.current = socket;

      socket.onopen = () => {
        if (disposed || wsRef.current !== socket) {
          return;
        }
        socketOpen = true;
        disconnectNotified = false;
        retryDelay = SPECTRUM_WS_RETRY_MIN_MS;
        setWsConnected(true);
        setWsReconnecting(false);
        setStage((current) => (current === 'recognized' ? 'testing' : current));
      };

      socket.onclose = () => {
        if (disposed || wsRef.current !== socket) {
          return;
        }
        socketOpen = false;
        setWsConnected(false);
        setWsReconnecting(true);
        scheduleReconnect();
      };

      // onerror 之后浏览器一定会再发一次 onclose，重连统一交给 onclose，别连两次。
      socket.onerror = () => {
        if (disposed || wsRef.current !== socket) {
          return;
        }
        socketOpen = false;
        setWsConnected(false);
        if (!disconnectNotified) {
          disconnectNotified = true;
          notifyInfo('等待光谱仪连接');
        }
      };

      socket.onmessage = handleMessage;
    }

    // 切回页面立刻重连：不只补已经断开的连接，握手一直悬着没返回的也一并重来。
    const reconnectNow = () => {
      if (disposed || document.hidden || socketOpen) {
        return;
      }
      retryDelay = SPECTRUM_WS_RETRY_MIN_MS;
      connect();
    };

    connect();
    // 只认「页面重新可见」和 bfcache 恢复。不监听 window 的 focus —— 这一页为了不丢
    // 扫码枪的字会不停 blur()→focus()，某些实现会因此在 window 上派发 focus，重连就会
    // 绕过退避、跟着聚焦循环猛敲服务器。
    document.addEventListener('visibilitychange', reconnectNow);
    window.addEventListener('pageshow', reconnectNow);

    return () => {
      disposed = true;
      window.clearTimeout(retryTimer);
      document.removeEventListener('visibilitychange', reconnectNow);
      window.removeEventListener('pageshow', reconnectNow);
      wsRef.current?.close();
      wsRef.current = null;
      setWsReconnecting(false);
    };
  }, [selectedDeviceId]);

  useEffect(() => {
    if (stage === 'recognized' && wsConnected) {
      setStage('testing');
    }
  }, [stage, wsConnected]);

  useEffect(() => () => {
    window.clearTimeout(passTimerRef.current);
    window.clearTimeout(manualInputModeTimerRef.current);
    window.clearTimeout(scanSubmitTimerRef.current);
    window.clearTimeout(scanKeyboardTimerRef.current);
    wsRef.current?.close();
  }, []);

  const applyScannedResult = useCallback((result: FinalReleasePmiTraceScanResult, code: string) => {
    window.clearTimeout(passTimerRef.current);
    setScanResult(result);
    setScanCode(code);

    const outcome = resolveScanOutcome(result);
    if (outcome === 'noPmi') {
      setStage('noPmi');
      notifyInfo('无需 PMI，继续扫码');
      passTimerRef.current = window.setTimeout(resetForNextScan, 1100);
      return;
    }

    if (outcome === 'alreadyDetected') {
      setStage('alreadyDetected');
      notifyInfo('该标签已检测');
      return;
    }

    if (outcome === 'passed') {
      rememberCompletedInspection(result, code);
      notifySuccess('检测已通过');
      resetForNextScan();
      return;
    }

    if (outcome === 'failed') {
      const items = buildPmiElementComparisonItems(result);
      setElementFilter(items.some((item) => item.state === 'fail') ? 'fail' : 'all');
      setStage('fail');
      notifyError('检测不通过');
      return;
    }

    setTraceCodes((current) => {
      if (current.includes(code)) {
        notifyInfo('该标签已在本次扫码记录中');
        return current;
      }
      return [...current, code];
    });
    setPmiMode('formal');
    setStage('testing');
    setWaitStartedAt(Date.now());
    notifySuccess(`已扫码 ${code}，等待光谱检测`);
  }, [rememberCompletedInspection, resetForNextScan]);

  function clearCurrentScans() {
    resetForNextScan();
    notifyInfo('已清空本次扫码');
  }

  function handleRetest() {
    const currentTraceCode = getTraceCode(scanResult, scanCode);
    if (!pmiGroups.length) {
      setTraceCodes((current) => (
        current.length || !currentTraceCode ? current : [currentTraceCode]
      ));
      if (currentTraceCode) {
        setPmiMode('formal');
      }
    }
    setPendingSpectrumPayload(null);
    lastPayloadRef.current = null;
    setScannerFeedback('正在等待新的光谱结果');
    setScannerFeedbackTone('info');
    setStage('testing');
    setWaitStartedAt(Date.now());
    notifyInfo(`正在重新检测${currentTraceCode ? `：${currentTraceCode}` : ''}`);
    window.setTimeout(() => manualInputRef.current?.focus({ preventScroll: true }), 80);
  }

  function handleViewExistingResult() {
    const tone = resolveInspectionTone(scanResult);
    const items = buildPmiElementComparisonItems(scanResult);
    setElementFilter(tone === 'fail' && items.some((item) => item.state === 'fail') ? 'fail' : 'all');
    setStage(tone === 'fail' ? 'fail' : 'pass');
  }

  function handleConfirmFailed() {
    if (scanResult) {
      rememberCompletedInspection(scanResult, scanCode);
    }
    notifyInfo('已确认不合格，请继续扫码');
    resetForNextScan();
  }

  function handleAppendPendingScan() {
    if (!pendingTraceScan) {
      return;
    }
    const { code } = pendingTraceScan;
    setTraceCodes((current) => (current.includes(code) ? current : [...current, code]));
    setPendingTraceScan(null);
    setPendingSpectrumPayload(null);
    lastPayloadRef.current = null;
    setStage('testing');
    setWaitStartedAt(Date.now());
    setScannerFeedback(`已追加追溯码：${code}`);
    setScannerFeedbackTone('success');
    notifySuccess(`已追加 ${code}，共用本次光谱结果`);
    window.setTimeout(() => manualInputRef.current?.focus({ preventScroll: true }), 80);
  }

  function handleReplacePendingScan() {
    if (!pendingTraceScan) {
      return;
    }
    const { code, result } = pendingTraceScan;
    setTraceCodes([code]);
    setScanCode(code);
    setScanResult(result);
    setPendingTraceScan(null);
    setPendingSpectrumPayload(null);
    lastPayloadRef.current = null;
    setStage('testing');
    setWaitStartedAt(Date.now());
    setScannerFeedback(`已替换为追溯码：${code}`);
    setScannerFeedbackTone('success');
    notifySuccess(`已替换为 ${code}，等待光谱检测`);
    window.setTimeout(() => manualInputRef.current?.focus({ preventScroll: true }), 80);
  }

  function handleCancelPendingScan() {
    setPendingTraceScan(null);
    setStage('testing');
    setScannerFeedback('已取消新标签，继续等待当前光谱结果');
    setScannerFeedbackTone('info');
    notifyInfo('已取消，继续检测当前标签');
    window.setTimeout(() => manualInputRef.current?.focus({ preventScroll: true }), 80);
  }

  async function openPmiRebindConfirm(code: string) {
    if (!traceCodes.length) {
      throw new Error('请先扫描 80×80 正式标签');
    }
    const response = await scanPmiRebind({ code });
    if (response.data.stage !== 'awaiting_formal_label' || !response.data.pmig?.code) {
      throw new Error(response.data.message || '该 PMIG 检测数据暂不能换绑');
    }
    setPendingPmiRebind({ code, validation: response.data });
    setStage('choice');
    setScannerFeedback(`PMIG ${code} 已有检测数据，请确认是否绑定`);
    setScannerFeedbackTone('info');
    notifyInfo('该 60×60 标签已有检测数据，请确认是否执行绑定');
  }

  async function handlePmiGroupScan(code: string) {
    if (pmiGroups.some((group) => group.code === code) || reboundPmigCodes.includes(code)) {
      notifyInfo('该 60×60 标签已在本次扫码记录中');
      setScannerFeedback(`PMIG ${code} 已在本次检测中`);
      setScannerFeedbackTone('info');
      setStage('testing');
      return;
    }

    if ((pmiMode === 'formal' || traceCodes.length) && pmiGroups.length >= 1) {
      const limitError = '当前正式检测已加入一个 60×60 PMIG，请先完成或取消本轮检测';
      notifyError(limitError);
      setScannerFeedback(limitError);
      setScannerFeedbackTone('error');
      setStage('testing');
      return;
    }

    if ((pmiMode === 'early' || (!traceCodes.length && pmiGroups.length > 0))
      && pmiGroups.length >= MAX_PMIG_CODES_PER_SPECTRUM) {
      const limitError = `一次光谱最多绑定 ${MAX_PMIG_CODES_PER_SPECTRUM} 个 60×60 PMIG，请先完成本轮检测`;
      notifyError(limitError);
      setScannerFeedback(limitError);
      setScannerFeedbackTone('error');
      setStage('testing');
      return;
    }

    const formalMode = pmiMode === 'formal' || traceCodes.length > 0;
    let response: Awaited<ReturnType<typeof scanPmiGroup>>;
    try {
      response = await scanPmiGroup(code);
    } catch (error) {
      if (formalMode) {
        await openPmiRebindConfirm(code);
        return;
      }
      throw error;
    }

    if (hasWaitingPmiGroup(response.data)) {
      const pmiInspection = await resolvePmiInspectionSource(response.data, code);
      setPmiMode((current) => (current === 'unset' ? 'early' : current));
      setPmiGroups((current) => [...current, { code, pmiInspection, result: response.data }]);
      if (!scanCode) {
        setScanCode(code);
      }
      setStage('testing');
      setWaitStartedAt(Date.now());
      setScannerFeedback(`已加入 60×60 标签：${code}`);
      setScannerFeedbackTone('success');
      notifySuccess(`已加入 ${code}，等待共用光谱结果`);
      return;
    }

    if (response.data.has_pmi_data && !formalMode) {
      const pmiInspection = await resolvePmiInspectionSource(response.data, code);
      const result = toPmiGroupHistoryResult(response.data, code);
      applyScannedResult({
        ...result,
        ...(hasPmiRangeSource(pmiInspection) ? { pmi_inspection: pmiInspection } : {}),
      }, code);
      return;
    }

    if (formalMode) {
      await openPmiRebindConfirm(code);
      return;
    }

    const statusMessage = response.data.message || 'PMIG 未进入等待光谱状态';
    notifyError(statusMessage);
    setScannerFeedback(`PMIG ${code}：${statusMessage}`);
    setScannerFeedbackTone('error');
    setStage('scanning');
  }

  async function handleConfirmPmiRebind() {
    if (!pendingPmiRebind || !traceCodes.length || binding) {
      return;
    }
    const targetTraceCode = traceCodes[0];
    setBinding(true);
    setScannerFeedback('正在执行 PMI 绑定...');
    setScannerFeedbackTone('info');
    try {
      const response = await scanPmiRebind({
        code: targetTraceCode,
        pmig_code: pendingPmiRebind.code,
      });
      if (response.data.stage !== 'completed') {
        throw new Error(response.data.message || 'PMI 绑定未完成');
      }
      setReboundPmigCodes((current) => (
        current.includes(pendingPmiRebind.code) ? current : [...current, pendingPmiRebind.code]
      ));
      setPendingPmiRebind(null);
      setStage('pass');
      setScannerFeedback('PMI 检测数据绑定完成');
      setScannerFeedbackTone('success');
      const pmiStatus = String(response.data.task?.pmi_status || '').toLowerCase();
      if (['fail', 'failed', 'ng'].includes(pmiStatus)) {
        notifyInfo('PMI 已绑定，但检测不合格，暂不可发货');
      } else {
        notifySuccess(response.data.idempotent ? 'PMI 检测数据已绑定' : 'PMI 换绑完成');
      }
      passTimerRef.current = window.setTimeout(resetForNextScan, 1200);
    } catch (error) {
      const errorMessage = getPmiFlowErrorMessage(error, 'PMI 绑定失败');
      notifyError(errorMessage);
      setScannerFeedback(errorMessage);
      setScannerFeedbackTone('error');
      setStage('choice');
    } finally {
      setBinding(false);
    }
  }

  function handleCancelPmiRebind() {
    setPendingPmiRebind(null);
    setStage('testing');
    setScannerFeedback('已取消绑定，继续等待当前光谱结果');
    setScannerFeedbackTone('info');
    notifyInfo('已取消绑定');
    window.setTimeout(() => manualInputRef.current?.focus({ preventScroll: true }), 80);
  }

  async function submitScanText(rawText: string) {
    window.clearTimeout(scanSubmitTimerRef.current);
    if (scanSubmittingRef.current || scanning || binding || pendingSpectrumPayload) {
      return;
    }

    const normalizedRawText = rawText.trim();
    if (!normalizedRawText) {
      focusScannerInput(0);
      return;
    }

    const now = Date.now();
    const lastSubmittedScan = lastSubmittedScanRef.current;
    if (lastSubmittedScan?.rawText === normalizedRawText
      && now - lastSubmittedScan.time < SCAN_INPUT_DEDUPE_WINDOW_MS) {
      return;
    }
    lastSubmittedScanRef.current = { rawText: normalizedRawText, time: now };
    scanKeyboardBufferRef.current = '';
    setScannerFeedback('正在解析扫码结果...');
    setScannerFeedbackTone('info');
    logPmiScannerPayload(normalizedRawText);
    const { code, errorMessage } = readUnifiedPmiCodeFromScanText(normalizedRawText);
    if (errorMessage) {
      notifyError(errorMessage);
      setScannerFeedback(errorMessage);
      setScannerFeedbackTone('error');
      setScanText('');
      setManualTraceCode('');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      setScanCode('');
      setStage('scanning');
      focusScannerInput(0);
      return;
    }

    if (!code) {
      focusScannerInput(0);
      return;
    }

    if (pmiMode === 'early' && FORMAL_TRACE_RE.test(code)) {
      const modeError = '请先结束当前提前检测，再扫描 80×80 正式标签';
      notifyError(modeError);
      setScannerFeedback(modeError);
      setScannerFeedbackTone('error');
      setScanText('');
      setManualTraceCode('');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      setStage('testing');
      focusScannerInput(0);
      return;
    }

    scanSubmittingRef.current = true;
    setScanning(true);
    setManualTraceCode('');
    const isPmigCode = PMIG_RE.test(code);
    setScannerFeedback(`已解析${isPmigCode ? 'PMIG' : '追溯码'}：${code}`);
    setScannerFeedbackTone('success');
    if (!getTraceCode(scanResult, scanCode)) {
      setScanCode(code);
    }
    notifyInfo(`已读取${isPmigCode ? ' PMIG' : '追溯码'}：${code}`);

    try {
      if (isPmigCode) {
        setScanText('');
        setManualTraceCode('');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        await handlePmiGroupScan(code);
        return;
      }

      const response = await scanFinalReleasePmiTrace(code);
      const pmiInspection = await resolvePmiInspectionSource(response.data, code);
      const result = applyPmiInspectionSource(response.data, pmiInspection);
      setScanText('');
      setManualTraceCode('');
      if (inputRef.current) {
        inputRef.current.value = '';
      }

      const outcome = resolveScanOutcome(result);
      if (traceCodes.length) {
        if (traceCodes.includes(code)) {
          notifyInfo('该标签已在本次扫码记录中');
          setScannerFeedback(`追溯码 ${code} 已在本次检测中`);
          setScannerFeedbackTone('info');
          setStage('testing');
          return;
        }
        if (outcome === 'waiting') {
          setPendingTraceScan({ code, result });
          setStage('choice');
          setScannerFeedback(`已识别新标签：${code}`);
          setScannerFeedbackTone('info');
          notifyInfo('请选择追加或替换当前标签');
          return;
        }
        if (outcome === 'noPmi') {
          notifyInfo(`追溯码 ${code} 无需 PMI，继续等待当前检测`);
          setScannerFeedback(`追溯码 ${code} 无需 PMI`);
          setScannerFeedbackTone('info');
          setStage('testing');
          return;
        }
        notifyInfo(`追溯码 ${code} 已有检测结果，继续等待当前检测`);
        setScannerFeedback(`追溯码 ${code} 已有检测结果`);
        setScannerFeedbackTone('info');
        setStage('testing');
        return;
      }
      applyScannedResult(result, code);
    } catch (error) {
      const errorMessage = getPmiFlowErrorMessage(error, '扫码失败');
      notifyError(errorMessage);
      setScannerFeedback(`追溯码 ${code}：${errorMessage}`);
      setScannerFeedbackTone('error');
      setStage(traceCodes.length ? 'testing' : 'scanning');
    } finally {
      scanSubmittingRef.current = false;
      setScanning(false);
      focusScannerInput(80);
    }
  }

  function handleScanTextInput(value: string) {
    setScanText(value);
    window.clearTimeout(scanSubmitTimerRef.current);

    const trimmedValue = value.trim();
    if (!trimmedValue || scanning || binding) {
      return;
    }

    setScannerFeedback('正在读取扫码内容...');
    setScannerFeedbackTone('info');
    if (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) {
      void submitScanText(trimmedValue);
      return;
    }

    scanSubmitTimerRef.current = window.setTimeout(() => {
      void submitScanText(trimmedValue);
    }, SCAN_AUTO_SUBMIT_DELAY_MS);
  }

  function handleManualScanTextInput(value: string) {
    setManualTraceCode(value);
    handleScanTextInput(value);
  }

  function handleScannerPaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const pastedText = event.clipboardData.getData('text');
    if (!pastedText.trim()) {
      return;
    }

    event.preventDefault();
    setScanText(pastedText);
    setScannerFeedback('正在读取扫码内容...');
    setScannerFeedbackTone('info');
    void submitScanText(pastedText);
  }

  async function handleScanSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await submitScanText(inputRef.current?.value ?? scanText);
  }

  async function handleManualTraceSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await submitScanText(manualTraceCode);
  }

  async function handleCameraScan(payload: QrmarkCameraPayload) {
    setScannerFeedback(`摄像头已识别：${payload.result || payload.qrText || '未读取到追溯码'}`);
    setScannerFeedbackTone(payload.result ? 'success' : 'error');
    await submitScanText(JSON.stringify(payload));
  }

  submitScanTextRef.current = submitScanText;

  useEffect(() => {
    const bridgeWindow = window as PdaScanBridgeWindow;
    const previousOnPdaScanResult = bridgeWindow.onPdaScanResult;
    const previousXsosPdaScan = bridgeWindow.XSOSPdaScan;
    const receiveNativeScan = (payload: unknown) => {
      const rawText = serializePdaScanPayload(payload);
      if (!rawText.trim()) {
        notifyError('扫码程序未返回数据');
        return;
      }
      setScannerFeedback('已通过扫码程序接收数据');
      setScannerFeedbackTone('info');
      void submitScanTextRef.current(rawText);
    };
    const handlePdaScanEvent = (event: Event) => {
      receiveNativeScan((event as CustomEvent).detail);
    };
    const handlePdaScanMessage = (event: MessageEvent) => {
      if (event.source && event.source !== window) {
        return;
      }
      const payload = extractPdaScanMessagePayload(event.data);
      if (payload !== null) {
        receiveNativeScan(payload);
      }
    };

    bridgeWindow.onPdaScanResult = receiveNativeScan;
    bridgeWindow.XSOSPdaScan = receiveNativeScan;
    window.addEventListener('pda-scan', handlePdaScanEvent);
    window.addEventListener('xsos:pda-scan', handlePdaScanEvent);
    window.addEventListener('message', handlePdaScanMessage);

    return () => {
      bridgeWindow.onPdaScanResult = previousOnPdaScanResult;
      bridgeWindow.XSOSPdaScan = previousXsosPdaScan;
      window.removeEventListener('pda-scan', handlePdaScanEvent);
      window.removeEventListener('xsos:pda-scan', handlePdaScanEvent);
      window.removeEventListener('message', handlePdaScanMessage);
    };
  }, []);

  useEffect(() => {
    if (!isInputEnabled) {
      scanKeyboardBufferRef.current = '';
      window.clearTimeout(scanKeyboardTimerRef.current);
      return undefined;
    }

    const flushKeyboardScan = () => {
      const rawText = scanKeyboardBufferRef.current;
      scanKeyboardBufferRef.current = '';
      window.clearTimeout(scanKeyboardTimerRef.current);
      if (rawText) {
        void submitScanTextRef.current(rawText);
      }
    };

    const handleGlobalScannerKey = (event: KeyboardEvent) => {
      if (event.target === deviceSelectRef.current
        || event.ctrlKey
        || event.metaKey
        || event.altKey
        || event.isComposing) {
        return;
      }

      if (event.key === 'Enter') {
        if (scanKeyboardBufferRef.current) {
          event.preventDefault();
          flushKeyboardScan();
        }
        return;
      }

      if (event.key === 'Backspace') {
        scanKeyboardBufferRef.current = scanKeyboardBufferRef.current.slice(0, -1);
      } else if (event.key.length === 1) {
        scanKeyboardBufferRef.current += event.key;
        setScannerFeedback('正在读取扫码内容...');
        setScannerFeedbackTone('info');
      } else {
        return;
      }

      window.clearTimeout(scanKeyboardTimerRef.current);
      scanKeyboardTimerRef.current = window.setTimeout(flushKeyboardScan, SCAN_AUTO_SUBMIT_DELAY_MS);
    };

    const focusHeartbeat = window.setInterval(() => {
      const focusTarget = manualInputRef.current || inputRef.current;
      if (!document.hidden
        && document.activeElement !== focusTarget
        && document.activeElement !== deviceSelectRef.current) {
        focusTarget?.focus({ preventScroll: true });
      }
    }, 500);

    document.addEventListener('keydown', handleGlobalScannerKey, true);
    focusScannerInput(0);

    return () => {
      document.removeEventListener('keydown', handleGlobalScannerKey, true);
      window.clearInterval(focusHeartbeat);
      window.clearTimeout(scanKeyboardTimerRef.current);
    };
  }, [focusScannerInput, isInputEnabled]);

  function handleDeviceChange(deviceId: string) {
    setSelectedDeviceId(deviceId);
    if (getTraceCode(scanResult, scanCode) || pmiGroups.length) {
      setWaitStartedAt(Date.now());
      notifyInfo(`已切换光谱仪：${deviceId}`);
    }
    window.setTimeout(() => {
      deviceSelectRef.current?.blur();
      if (isInputEnabled) {
        (manualInputRef.current || inputRef.current)?.focus({ preventScroll: true });
      }
    }, 0);
  }

  function handleDeviceRefresh() {
    if (deviceLoading) {
      return;
    }
    deviceSelectRef.current?.blur();
    setDeviceLoading(true);
    setDeviceRefreshKey((current) => current + 1);
  }

  const primaryPmigCode = pmiGroups[0]?.code || reboundPmigCodes[0] || '';
  const primaryCode = getTraceCode(scanResult, scanCode) || traceCodes[0] || primaryPmigCode;
  const traceText = primaryPmigCode && !traceCodes.length
    ? primaryPmigCode
    : getSerialText(scanResult, scanCode);
  const productInfo = getProductInfo(scanResult, scanCode);
  const lastProductInfo = getProductInfo(lastCompletedInspection?.result || null, lastCompletedInspection?.code || '');
  const lastInspectionGrade = getTraceGrade(lastCompletedInspection?.result || null);
  const scanRecordCodes = [
    ...traceCodes,
    ...pmiGroups.map((group) => group.code),
    ...reboundPmigCodes,
  ].filter((code, index, codes) => codes.indexOf(code) === index);
  const showSuccessBadge = stage !== 'scanning';
  const showElementPanel = stage === 'fail' || stage === 'pass';

  return (
    <PdaPage
      actionIcon={<History aria-hidden="true" size={17} strokeWidth={2.3} />}
      actionLabel="检测历史"
      onAction={() => navigateTo('/pmi-history')}
      onBack={showElementPanel ? resetForNextScan : () => navigateTo('/home')}
      pageClassName={`continuous-pmi-page continuous-pmi-page--${stage}${cameraScannerOpen ? ' continuous-pmi-page--camera-open' : ''}`}
      title={showElementPanel ? '元素详情' : 'PMI 标签检测'}
    >
      <form
        className={`continuous-pmi-hidden-form${isInputEnabled ? '' : ' continuous-pmi-hidden-form--disabled'}`}
        data-testid="continuous-pmi-form"
        onSubmit={handleScanSubmit}
      >
        <input
          ref={inputRef}
          aria-label="连续 PMI 扫码输入"
          autoFocus
          autoCapitalize="none"
          autoComplete="off"
          className="continuous-pmi-hidden-input"
          disabled={!isInputEnabled}
          inputMode="none"
          onBlur={() => {
            setScannerFocused(false);
            focusScannerInput(40);
          }}
          onChange={(event) => handleScanTextInput(event.target.value)}
          onFocus={() => setScannerFocused(true)}
          onInput={(event) => handleScanTextInput(event.currentTarget.value)}
          onKeyUp={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void submitScanText(event.currentTarget.value || scanKeyboardBufferRef.current);
            }
          }}
          onPaste={handleScannerPaste}
        />
      </form>

      <section
        className={`continuous-pmi-hero${cameraScannerOpen ? ' continuous-pmi-hero--camera-open' : ''}`}
        aria-live="polite"
      >
        <div className="continuous-pmi-orbit" aria-hidden="true">
          <span className="continuous-pmi-orbit__ring continuous-pmi-orbit__ring--outer" />
          <span className="continuous-pmi-orbit__ring continuous-pmi-orbit__ring--middle" />
          <span className="continuous-pmi-orbit__ring continuous-pmi-orbit__ring--inner" />
        </div>

        <div className="continuous-pmi-scanner">
          <span className="continuous-pmi-corner continuous-pmi-corner--tl" />
          <span className="continuous-pmi-corner continuous-pmi-corner--tr" />
          <span className="continuous-pmi-corner continuous-pmi-corner--bl" />
          <span className="continuous-pmi-corner continuous-pmi-corner--br" />
          <span className="continuous-pmi-scanline" />
          {showSuccessBadge ? (
            <span className="continuous-pmi-result-mark" aria-hidden="true">
              {stage === 'fail' ? <X size={38} /> : <Check size={40} />}
            </span>
          ) : null}

          <div className="continuous-pmi-barcode" aria-hidden="true">
            {Array.from({ length: 26 }).map((_, index) => (
              <span key={index} style={{ width: `${index % 4 === 0 ? 5 : index % 3 === 0 ? 3 : 2}px` }} />
            ))}
          </div>
          <div className="continuous-pmi-serial">{traceText}</div>
          {primaryCode ? (
            <div className="continuous-pmi-card-line">
              <span>
                <ClipboardList size={18} />
                {primaryPmigCode && !traceCodes.length ? 'PMIG' : '追溯码'}
              </span>
              <strong>{primaryCode}</strong>
            </div>
          ) : null}
        </div>

        <div className="continuous-pmi-state">
          <div className="continuous-pmi-state__title">{getStatusText(stage)}</div>
          <div className="continuous-pmi-state__sub">{getSubText(stage, Boolean(scanResult?.can_retest))}</div>
          {primaryCode && stage !== 'scanning' ? (
            <div className="continuous-pmi-chip">
              <MapPin size={18} />
              标签已识别
            </div>
          ) : (
            <>
              <div className="continuous-pmi-scan-pill">
                <span aria-hidden="true" />
                80×80 / 60×60 PMI 标签
              </div>
              <div className={`continuous-pmi-input-status continuous-pmi-input-status--${scannerFeedbackTone}`}>
                <span aria-hidden="true" />
                {scannerFeedback || (scannerFocused ? '扫码焦点已锁定' : '正在激活扫码焦点')}
              </div>
            </>
          )}
        </div>

        {['scanning', 'recognized', 'testing'].includes(stage) ? (
          <QrmarkCameraScanner
            disabled={!canScanLabel}
            embedded
            onOpenChange={setCameraScannerOpen}
            onScan={handleCameraScan}
            preloadEngines
          />
        ) : null}

        {['scanning', 'recognized', 'testing'].includes(stage) ? (
          <form className="continuous-pmi-manual" onSubmit={handleManualTraceSubmit}>
            <span className="continuous-pmi-manual__tag">临时测试</span>
            <label className="continuous-pmi-manual__field">
              <span>追溯码</span>
              <input
                ref={manualInputRef}
                aria-label="临时追溯码"
                autoFocus
                autoCapitalize="none"
                autoComplete="off"
                autoCorrect="off"
                inputMode={manualInputMode}
                onBlur={() => {
                  window.clearTimeout(manualInputModeTimerRef.current);
                  setManualInputMode('none');
                  setScannerFocused(false);
                  focusScannerInput(40);
                }}
                onChange={(event) => handleManualScanTextInput(event.target.value)}
                onFocus={() => {
                  setScannerFocused(true);
                  window.clearTimeout(manualInputModeTimerRef.current);
                  manualInputModeTimerRef.current = window.setTimeout(() => {
                    if (document.activeElement === manualInputRef.current) {
                      setManualInputMode('numeric');
                    }
                  }, 80);
                }}
                onInput={(event) => handleManualScanTextInput(event.currentTarget.value)}
                placeholder="扫描或输入 PMI 标签码"
                spellCheck={false}
                value={manualTraceCode}
              />
            </label>
            <button disabled={!manualTraceCode.trim() || scanning || binding} type="submit">
              <Check aria-hidden="true" size={18} strokeWidth={2.4} />
              {stage === 'scanning' ? '确认' : '测试扫描'}
            </button>
          </form>
        ) : null}
      </section>

      <section className="continuous-pmi-device">
        <div className="continuous-pmi-device__title">
          <Radio aria-hidden="true" size={20} strokeWidth={2.3} />
          <div>
            <div className="continuous-pmi-device__label-row">
              <span>光谱仪设备</span>
              <span className={wsConnected ? 'continuous-pmi-device__status continuous-pmi-device__status--online' : 'continuous-pmi-device__status'}>
                {!selectedDeviceId ? '未连接'
                  : wsConnected ? '已连接'
                    : wsReconnecting ? '重连中' : '连接中'}
              </span>
            </div>
            <strong>{deviceLoading ? '正在加载设备' : xrfDevices.length ? '当前检测设备' : '暂无可用设备'}</strong>
          </div>
        </div>
        <div className="continuous-pmi-device__control">
          <button
            aria-label="刷新光谱仪设备"
            className="continuous-pmi-device__refresh"
            disabled={deviceLoading}
            onClick={handleDeviceRefresh}
            title="刷新光谱仪设备"
            type="button"
          >
            <RefreshCw aria-hidden="true" className={deviceLoading ? 'is-loading' : ''} size={16} strokeWidth={2.4} />
          </button>
          <div className="continuous-pmi-device__select">
            <select
              ref={deviceSelectRef}
              aria-label="光谱仪设备"
              disabled={!xrfDevices.length}
              onChange={(event) => handleDeviceChange(event.target.value)}
              value={selectedDeviceId}
            >
              {!xrfDevices.length ? (
                <option value="">
                  {deviceLoading ? '加载中...' : '暂无设备'}
                </option>
              ) : null}
              {xrfDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.deviceId}
                </option>
              ))}
            </select>
            <ChevronDown aria-hidden="true" className="continuous-pmi-device__chevron" size={17} strokeWidth={2.4} />
          </div>
        </div>
      </section>

      {productInfo && stage !== 'scanning' ? (
        <section className="continuous-pmi-product">
          <div className="continuous-pmi-product__head">
            <div>
              <span>产品</span>
              <strong>{productInfo.name}</strong>
            </div>
            {productInfo.grade ? <em>{productInfo.grade}</em> : null}
          </div>
          {productInfo.fields.length ? (
            <div className="continuous-pmi-product__grid">
              {productInfo.fields.map((field) => (
                <div className="continuous-pmi-product__field" key={field.label}>
                  <span>{field.label}</span>
                  <strong>{field.value}</strong>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {stage === 'choice' && pendingTraceScan ? (
        <div className="continuous-pmi-choice-backdrop" role="presentation">
          <section
            aria-describedby="continuous-pmi-choice-description"
            aria-labelledby="continuous-pmi-choice-title"
            aria-modal="true"
            className="continuous-pmi-choice-dialog"
            role="dialog"
          >
            <div className="continuous-pmi-choice-dialog__eyebrow">检测进行中</div>
            <h2 id="continuous-pmi-choice-title">扫到另一张标签</h2>
            <p id="continuous-pmi-choice-description">
              当前 {traceCodes.join('、')} · 新标签 {pendingTraceScan.code}
            </p>
            <div className="continuous-pmi-choice-dialog__actions">
              <button className="continuous-pmi-choice-dialog__append" onClick={handleAppendPendingScan} type="button">
                <strong>追加到本次检测</strong>
                <span>多张标签共用同一个光谱结果</span>
              </button>
              <button onClick={handleReplacePendingScan} type="button">
                <strong>替换当前标签</strong>
                <span>只保留新标签等待检测</span>
              </button>
            </div>
            <button className="continuous-pmi-choice-dialog__cancel" onClick={handleCancelPendingScan} type="button">
              取消，继续等待当前标签
            </button>
          </section>
        </div>
      ) : null}

      {pendingPmiRebind ? (
        <div className="continuous-pmi-choice-backdrop" role="presentation">
          <section
            aria-describedby="continuous-pmi-rebind-description"
            aria-labelledby="continuous-pmi-rebind-title"
            aria-modal="true"
            className="continuous-pmi-choice-dialog"
            role="dialog"
          >
            <div className="continuous-pmi-choice-dialog__eyebrow">该 60×60 标签已有检测数据</div>
            <h2 id="continuous-pmi-rebind-title">是否执行绑定？</h2>
            <p id="continuous-pmi-rebind-description">
              确认后执行现有 PMI 换绑功能，不再等待新的光谱数据。
            </p>
            <div className="continuous-pmi-rebind-map">
              <div>
                <span>60×60 PMIG</span>
                <strong>{pendingPmiRebind.validation.pmig.code}</strong>
              </div>
              <i aria-hidden="true">→</i>
              <div>
                <span>80×80 正式标签</span>
                <strong>{traceCodes[0]}</strong>
              </div>
            </div>
            <div className="continuous-pmi-rebind-actions">
              <button onClick={handleCancelPmiRebind} type="button">取消</button>
              <button disabled={binding} onClick={() => void handleConfirmPmiRebind()} type="button">
                {binding ? '正在绑定...' : '确认绑定'}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <section className="continuous-pmi-scan-records" aria-label="扫码记录区">
        <div className="continuous-pmi-scan-records__head">
          <div>
            <span>扫码记录区</span>
            <strong>当前已扫标签数量：{scanRecordCodes.length}</strong>
          </div>
          <button disabled={!scanRecordCodes.length || binding} onClick={clearCurrentScans} type="button">
            清空本次扫码
          </button>
        </div>
        {scanRecordCodes.length ? (
          <div className="continuous-pmi-scan-records__list">
            {scanRecordCodes.map((code, index) => (
              <div key={code}>
                <span>{index + 1}</span>
                <strong>{code}</strong>
                <em>{PMIG_RE.test(code) ? '60×60' : '80×80'}</em>
              </div>
            ))}
          </div>
        ) : (
          <div className="continuous-pmi-scan-records__empty">请连续扫描需要共用一次光谱结果的标签</div>
        )}
      </section>

      {!showElementPanel && lastCompletedInspection ? (
        <section
          aria-label="上一次检测元素详情"
          className={`continuous-pmi-last-result continuous-pmi-last-result--${lastCompletedInspection.tone}`}
        >
          <div className="continuous-pmi-last-result__head">
            <div>
              <span>上一次检测</span>
              <strong>{lastCompletedInspection.code || lastProductInfo?.name || 'PMI 检测结果'}</strong>
              {lastInspectionGrade ? <small>{lastInspectionGrade}</small> : null}
            </div>
            <em>{lastCompletedInspection.tone === 'fail' ? '不合格' : '合格'}</em>
          </div>
          <PmiElementComparison
            className="continuous-pmi-last-result__elements"
            filter={lastElementFilter}
            items={lastComparisonItems}
            onFilterChange={setLastElementFilter}
          />
        </section>
      ) : null}

      {!showElementPanel ? (
        <button
          className="continuous-pmi-more-history"
          onClick={() => navigateTo('/pmi-history')}
          type="button"
        >
          <History aria-hidden="true" size={18} strokeWidth={2.3} />
          查看更多历史
        </button>
      ) : null}

      {stage === 'alreadyDetected' ? (
        <div className="continuous-pmi-choice-backdrop" role="presentation">
          <section
            aria-describedby="continuous-pmi-detected-description"
            aria-labelledby="continuous-pmi-detected-title"
            aria-modal="true"
            className="continuous-pmi-choice-dialog"
            role="dialog"
          >
            <div className="continuous-pmi-choice-dialog__eyebrow">重复标签</div>
            <h2 id="continuous-pmi-detected-title">该标签已检测</h2>
            <p id="continuous-pmi-detected-description">追溯码 {traceText}</p>
            <div className="continuous-pmi-choice-dialog__actions">
              <button
                autoFocus
                className="continuous-pmi-choice-dialog__append"
                onClick={handleViewExistingResult}
                type="button"
              >
                <strong>查看记录</strong>
                <span>查看之前的检测结果</span>
              </button>
              {scanResult?.can_retest ? (
                <button onClick={handleRetest} type="button">
                  <strong>复检</strong>
                  <span>当前追溯码重新等待光谱结果</span>
                </button>
              ) : null}
            </div>
            <button className="continuous-pmi-choice-dialog__cancel" onClick={resetForNextScan} type="button">
              继续扫描下一张标签
            </button>
          </section>
        </div>
      ) : null}

      {showElementPanel ? (
        <PmiElementComparison
          className="continuous-pmi-element-page"
          filter={elementFilter}
          items={comparisonItems}
          onFilterChange={setElementFilter}
          footer={(
            <div className="continuous-pmi-fail-actions">
              <button
                className="continuous-pmi-fail-actions__confirm"
                onClick={stage === 'fail' ? handleConfirmFailed : resetForNextScan}
                type="button"
              >
                {stage === 'fail' ? '确认不合格' : '继续扫描'}
              </button>
              {scanResult?.can_retest ? (
                <button className="pmi-primary-btn" onClick={handleRetest} type="button">
                  重新检测
                </button>
              ) : null}
            </div>
          )}
        />
      ) : null}
    </PdaPage>
  );
}

export default ContinuousPmiScan;
